import {
  Page,
  Layout,
  Card,
  Text,
  Banner,
  BlockStack,
  InlineGrid,
  DataTable,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useAnalytics } from '../hooks/useAnalytics';
import ReviewPromptBanner from '../components/ReviewPromptBanner';
import { useLanguage } from '../providers/LanguageProvider';

export default function AnalyticsPage() {
  const { report, loading, error } = useAnalytics();
  const { t, language } = useLanguage();
  const dateLocale = language === 'it' ? 'it-IT' : 'en-US';

  if (loading) {
    return (
      <Page title={t.analytics.title}>
        <Layout><Layout.Section><Card><SkeletonBodyText lines={6} /></Card></Layout.Section></Layout>
      </Page>
    );
  }

  if (error || !report) {
    return (
      <Page title={t.analytics.title}>
        <Layout><Layout.Section><Banner tone="warning"><p>{t.analytics.loadError}</p></Banner></Layout.Section></Layout>
      </Page>
    );
  }

  const { funnel, usage, attribution, comparison } = report;
  const funnelRows: [string, string][] = [
    [t.analytics.stageViewed, funnel.viewers.toString()],
    [t.analytics.stageTriedOn, funnel.tryOnUsers.toString()],
    [t.analytics.stageSized, funnel.findMySizeUsers.toString()],
    [t.analytics.stageUsedAisthetix, funnel.usedAisthetixUsers.toString()],
    [t.analytics.stageAddedToCart, funnel.addedToCart.toString()],
    [t.analytics.stagePurchased, funnel.purchasers.toString()],
  ];
  const usageRows: [string, string][] = [
    [t.analytics.tryOnOnly, usage.tryOnOnly.toString()],
    [t.analytics.findMySizeOnly, usage.findMySizeOnly.toString()],
    [t.analytics.bothFeatures, usage.both.toString()],
    [t.analytics.usedAisthetix, usage.usedAisthetix.toString()],
  ];
  const formatRate = (value: number) => new Intl.NumberFormat(dateLocale, {
    style: 'percent', maximumFractionDigits: 1,
  }).format(value);
  const revenueFormatted = new Intl.NumberFormat(dateLocale, {
    style: 'currency', currency: attribution.currency || 'USD',
  }).format(attribution.attributedRevenue);

  return (
    <Page title={t.analytics.title} subtitle={t.analytics.subtitle(report.windowDays)}>
      <Layout>
        <ReviewPromptBanner source="analytics" />
        <Layout.Section>
          <Banner tone="info"><p>{t.analytics.correlationalDisclaimer}</p></Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">{t.analytics.funnelTitle}</Text>
              <Text as="p" tone="subdued" variant="bodySm">{t.analytics.funnelHelp}</Text>
              <DataTable columnContentTypes={['text', 'numeric']} headings={[t.analytics.stage, t.analytics.shoppers]} rows={funnelRows} />
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">{t.analytics.usageTitle}</Text>
              <Text as="p" tone="subdued" variant="bodySm">{t.analytics.usageHelp}</Text>
              <DataTable columnContentTypes={['text', 'numeric']} headings={[t.analytics.stage, t.analytics.shopperProductPairs]} rows={usageRows} />
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">{t.analytics.comparisonTitle}</Text>
              {comparison.sufficientData ? (
                <BlockStack gap="300">
                  <figure aria-label={t.analytics.comparisonTitle} style={{ margin: 0 }}>
                    {[
                      { label: t.analytics.usedLabel, rate: comparison.usedRate, cohort: comparison.usedCohort, color: '#303030' },
                      { label: t.analytics.didNotUseLabel, rate: comparison.baselineRate, cohort: comparison.baselineCohort, color: '#8a8a8a' },
                    ].map((row) => (
                      <div key={row.label} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <span>{row.label} (n={row.cohort})</span>
                          <strong>{formatRate(row.rate)}</strong>
                        </div>
                        <div style={{ height: 18, background: '#e3e3e3', borderRadius: 9, overflow: 'hidden', marginTop: 6 }}>
                          <div style={{ width: `${Math.max(0, Math.min(1, row.rate)) * 100}%`, height: '100%', background: row.color }} />
                        </div>
                      </div>
                    ))}
                  </figure>
                  <Text as="p" variant="headingLg">{t.analytics.comparisonResult(comparison.rateDifference * 100)}</Text>
                  {!comparison.ratioComputable && <Text as="p" tone="subdued" variant="bodySm">{t.analytics.ratioUnavailable}</Text>}
                  <Text as="p" tone="subdued" variant="bodySm">{t.analytics.liftCausalCaveat}</Text>
                </BlockStack>
              ) : (
                <Banner tone="info"><p>{t.analytics.collectingData(comparison.minCohort)}</p></Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">{t.analytics.revenueTitle}</Text>
              <InlineGrid columns={2} gap="400">
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">{t.analytics.attributedRevenue}</Text>
                  <Text as="p" variant="headingLg">{revenueFormatted}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">{t.analytics.attributedOrders}</Text>
                  <Text as="p" variant="headingLg">{attribution.attributedOrders.toString()}</Text>
                </BlockStack>
              </InlineGrid>
              <Text as="p" tone="subdued" variant="bodySm">{t.analytics.revenueHelp}</Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
